<template>
    <BaseTemplate app-name="Entrega de Exames" page-title="">
        <template #content>
            <div class="px-6 pt-6">

            <!-- Banner Message -->
            <BannerMessage 
                v-if="showBanner"
                :type="bannerType"
                :message="bannerMessage"
                @close="showBanner = false"
            />

            <!-- Search and Filters -->
            <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-6 border border-gray-200 dark:border-gray-700">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo de Busca</label>
                        <select v-model="searchType" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" :style="{ '--tw-ring-color': 'var(--clinic-btn-bg)' }">
                            <option value="patient_name">Nome do Paciente</option>
                            <option value="patient_id">ID do Paciente</option>
                            <option value="accession_number">Número da OS</option>
                            <option value="patient_cpf">CPF do Paciente</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Termo de Busca</label>
                        <input 
                            v-model="searchQuery" 
                            type="text" 
                            placeholder="Digite para buscar..."
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            :style="{ '--tw-ring-color': 'var(--clinic-btn-bg)' }"
                            @keyup.enter="searchDeliveries"
                        >
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                        <select v-model="statusFilter" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" :style="{ '--tw-ring-color': 'var(--clinic-btn-bg)' }">
                            <option value="">Todos</option>
                            <option value="pending">Pendente</option>
                            <option value="ready">Pronto para Entrega</option>
                            <option value="delivered">Entregue</option>
                        </select>
                    </div>
                </div>
                <div class="flex gap-2">
                    <button 
                        @click="searchDeliveries"
                        class="px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 transition-colors"
                        :style="{ backgroundColor: 'var(--clinic-btn-bg)', '--tw-ring-color': 'var(--clinic-btn-bg)' }"
                        @mouseenter="$event.target.style.backgroundColor = 'var(--clinic-btn-bg-hover)'"
                        @mouseleave="$event.target.style.backgroundColor = 'var(--clinic-btn-bg)'"
                    >
                        Buscar
                    </button>
                    <button 
                        @click="clearFilters"
                        class="px-4 py-2 bg-gray-600 dark:bg-gray-700 text-white rounded-md hover:bg-gray-700 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                    >
                        Limpar
                    </button>
                </div>
            </div>

            <!-- Deliveries List -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
                <div class="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">Lista de Entregas</h2>
                </div>
                
                <div v-if="loading" class="p-6 text-center">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" :style="{ borderColor: 'var(--clinic-btn-bg)' }"></div>
                    <p class="mt-2 text-gray-600 dark:text-gray-400">Carregando...</p>
                </div>

                <div v-else-if="deliveries.length === 0" class="p-6 text-center">
                    <p class="text-gray-500 dark:text-gray-400">Nenhuma entrega encontrada</p>
                </div>

                <div v-else class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Paciente</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Exame</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">OS</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Data</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Impressão</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            <tr v-for="delivery in deliveries" :key="delivery.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div>
                                        <div class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ delivery.patient_name }}</div>
                                        <div class="text-sm text-gray-500 dark:text-gray-400">ID: {{ delivery.patient_id }}</div>
                                        <div v-if="delivery.patient_cpf" class="text-sm text-gray-500 dark:text-gray-400">{{ formatCPF(delivery.patient_cpf) }}</div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm text-gray-900 dark:text-gray-100">{{ delivery.service_name }}</div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm text-gray-900 dark:text-gray-100">{{ delivery.accession_number || '-' }}</div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm text-gray-900 dark:text-gray-100">{{ formatDate(delivery.exam_date) }}</div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span :class="getStatusClass(delivery.status)" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                                        {{ getStatusText(delivery.status) }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="flex space-x-2">
                                        <span :class="delivery.images_printed ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'" class="px-2 py-1 text-xs rounded-full">
                                            Imagens
                                        </span>
                                        <span :class="delivery.report_printed ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'" class="px-2 py-1 text-xs rounded-full">
                                            Laudo
                                        </span>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <div class="flex space-x-2">
                                        <button 
                                            v-if="delivery.status === 'ready'"
                                            @click="openDeliveryModal(delivery)"
                                            class="px-3 py-1 rounded-md text-sm text-white transition-colors"
                                            :style="{ backgroundColor: 'var(--clinic-btn-bg)' }"
                                            @mouseenter="$event.target.style.backgroundColor = 'var(--clinic-btn-bg-hover)'"
                                            @mouseleave="$event.target.style.backgroundColor = 'var(--clinic-btn-bg)'"
                                        >
                                            Imprimir
                                        </button>
                                        <button 
                                            v-if="delivery.status === 'pending'"
                                            @click="markAsReady(delivery)"
                                            class="px-3 py-1 rounded-md text-sm text-white transition-colors"
                                            :style="{ backgroundColor: 'var(--clinic-btn-bg)' }"
                                            @mouseenter="$event.target.style.backgroundColor = 'var(--clinic-btn-bg-hover)'"
                                            @mouseleave="$event.target.style.backgroundColor = 'var(--clinic-btn-bg)'"
                                        >
                                            Marcar Pronto
                                        </button>
                                        <button 
                                            v-if="delivery.status === 'delivered'"
                                            @click="viewDeliveryDetails(delivery)"
                                            class="px-3 py-1 rounded-md text-sm text-white transition-colors"
                                            :style="{ backgroundColor: 'var(--clinic-btn-bg)' }"
                                            @mouseenter="$event.target.style.backgroundColor = 'var(--clinic-btn-bg-hover)'"
                                            @mouseleave="$event.target.style.backgroundColor = 'var(--clinic-btn-bg)'"
                                        >
                                            <EyeIcon class="w-4 h-4 inline mr-1" />
                                            Ver
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div v-if="deliveries.length > 0" class="px-6 py-3 border-t border-gray-200 dark:border-gray-700">
                    <div class="flex items-center justify-between">
                        <div class="text-sm text-gray-700 dark:text-gray-300">
                            Mostrando {{ pagination.offset + 1 }} a {{ pagination.offset + deliveries.length }} de {{ pagination.count }} resultados
                        </div>
                        <div class="flex space-x-2">
                            <button 
                                @click="previousPage"
                                :disabled="pagination.offset === 0"
                                class="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                            >
                                Anterior
                            </button>
                            <button 
                                @click="nextPage"
                                :disabled="pagination.offset + deliveries.length >= pagination.count"
                                class="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                            >
                                Próximo
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Delivery Modal -->
            <Transition
                enter-active-class="transition duration-300 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-200 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
            >
                <div v-if="showDeliveryModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click="closeDeliveryModal">
                    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-xl w-full mx-4 border border-gray-200 dark:border-gray-700 transform transition-all" @click.stop>
                        <!-- Header -->
                        <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                            <div class="flex items-center space-x-3">
                                <div class="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                                    <PackageCheckIcon class="w-5 h-5 text-green-600 dark:text-green-400" />
                                </div>
                                <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Confirmar Entrega</h3>
                            </div>
                            <button 
                                @click="closeDeliveryModal"
                                class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                aria-label="Fechar modal"
                            >
                                <XIcon class="w-4 h-4" />
                            </button>
                        </div>
                        
                        <!-- Content -->
                        <div class="p-4">
                            <!-- Patient Info -->
                            <div v-if="selectedDelivery" class="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                                <div class="flex items-center space-x-3 mb-3">
                                    <UserIcon class="w-5 h-5 text-gray-500" />
                                    <h4 class="font-medium text-gray-900 dark:text-gray-100">Informações do Paciente</h4>
                                </div>
                                <div class="space-y-2 text-sm">
                                    <div class="flex justify-between">
                                        <span class="text-gray-600 dark:text-gray-400">Paciente:</span>
                                        <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedDelivery.patient_name }}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600 dark:text-gray-400">Exame:</span>
                                        <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedDelivery.service_name }}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600 dark:text-gray-400">OS:</span>
                                        <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedDelivery.accession_number || 'N/A' }}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600 dark:text-gray-400">Data:</span>
                                        <span class="font-medium text-gray-900 dark:text-gray-100">{{ formatDate(selectedDelivery.exam_date) }}</span>
                                    </div>
                                </div>
                            </div>
                            
                            <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
                                Preencha as informações abaixo para confirmar a entrega e imprimir o exame:
                            </p>
                            
                            <form @submit.prevent="confirmDelivery" class="space-y-3">
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            <div class="flex items-center space-x-2">
                                                <UserIcon class="w-4 h-4 text-gray-500" />
                                                <span>Nome de quem recebeu</span>
                                                <span class="text-red-500">*</span>
                                            </div>
                                        </label>
                                        <input 
                                            v-model="deliveryForm.delivered_to" 
                                            type="text" 
                                            required
                                            placeholder="Digite o nome completo"
                                            :class="[
                                                'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 transition-colors',
                                                deliveryForm.delivered_to 
                                                    ? 'border-green-300 dark:border-green-600 focus:ring-green-500' 
                                                    : 'border-gray-300 dark:border-gray-600 focus:ring-green-500'
                                            ]"
                                        >
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            <div class="flex items-center space-x-2">
                                                <IdCardIcon class="w-4 h-4 text-gray-500" />
                                                <span>Documento</span>
                                                <span class="text-red-500">*</span>
                                            </div>
                                        </label>
                                        <input 
                                            v-model="deliveryForm.delivered_to_document" 
                                            type="text" 
                                            required
                                            placeholder="CPF, RG ou outro documento"
                                            :class="[
                                                'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 transition-colors',
                                                deliveryForm.delivered_to_document 
                                                    ? 'border-green-300 dark:border-green-600 focus:ring-green-500' 
                                                    : 'border-gray-300 dark:border-gray-600 focus:ring-green-500'
                                            ]"
                                        >
                                    </div>
                                </div>
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            <div class="flex items-center space-x-2">
                                                <UsersIcon class="w-4 h-4 text-gray-500" />
                                                <span>Parentesco/Relacionamento</span>
                                                <span class="text-red-500">*</span>
                                            </div>
                                        </label>
                                        <input 
                                            v-model="deliveryForm.relationship" 
                                            type="text" 
                                            required
                                            placeholder="Ex: Filho, Cônjuge, Amigo"
                                            :class="[
                                                'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 transition-colors',
                                                deliveryForm.relationship 
                                                    ? 'border-green-300 dark:border-green-600 focus:ring-green-500' 
                                                    : 'border-gray-300 dark:border-gray-600 focus:ring-green-500'
                                            ]"
                                        >
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            <div class="flex items-center space-x-2">
                                                <StickyNoteIcon class="w-4 h-4 text-gray-500" />
                                                <span>Observações</span>
                                            </div>
                                        </label>
                                        <textarea 
                                            v-model="deliveryForm.delivery_notes" 
                                            rows="2"
                                            placeholder="Observações adicionais sobre a entrega..."
                                            :class="[
                                                'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 transition-colors resize-none',
                                                deliveryForm.delivery_notes 
                                                    ? 'border-green-300 dark:border-green-600 focus:ring-green-500' 
                                                    : 'border-gray-300 dark:border-gray-600 focus:ring-green-500'
                                            ]"
                                        ></textarea>
                                    </div>
                                </div>
                            </form>
                            
                            <!-- Help text -->
                            <div class="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                <div class="flex items-start space-x-2">
                                    <InfoIcon class="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                                    <div class="text-sm text-blue-700 dark:text-blue-300">
                                        <p class="font-medium mb-1">Informações importantes:</p>
                                        <ul class="space-y-1 text-xs">
                                            <li>• Os campos marcados com * são obrigatórios para confirmar a entrega</li>
                                            <li>• Preencha com informações completas e precisas</li>
                                            <li>• As observações são opcionais e podem detalhar como foi realizada a entrega</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            
            
                            
                            <!-- Message when form is not valid -->
                            <div v-if="!isDeliveryFormValid" class="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                <div class="flex items-center space-x-2">
                                    <AlertCircleIcon class="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                                    <span class="text-sm text-yellow-700 dark:text-yellow-300">
                                        Preencha todos os campos obrigatórios para acessar as opções de impressão
                                    </span>
                                </div>
                            </div>

                            <!-- Print Options - Show when form is valid -->
                            <div v-if="isDeliveryFormValid" class="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                <div class="flex items-center space-x-2 mb-3">
                                    <InfoIcon class="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                    <span class="text-sm font-medium text-blue-700 dark:text-blue-300">
                                        Dados preenchidos! Agora você pode imprimir o exame:
                                    </span>
                                </div>
                                <div class="flex space-x-3">
                                    <button 
                                        @click="printImages"
                                        :disabled="printLoading"
                                        class="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 flex items-center justify-center space-x-2"
                                    >
                                        <ImageIcon v-if="!printLoading" class="w-4 h-4" />
                                        <div v-else class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>{{ printLoading ? 'Imprimindo...' : 'Imprimir Imagem' }}</span>
                                    </button>
                                    <button 
                                        @click="printReport"
                                        :disabled="downloadLoading || !selectedDelivery.has_medical_report"
                                        class="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 flex items-center justify-center space-x-2"
                                    >
                                        <FileTextIcon v-if="!downloadLoading" class="w-4 h-4" />
                                        <div v-else class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>{{ downloadLoading ? 'Imprimindo...' : 'Imprimir Laudo' }}</span>
                                    </button>
                                </div>
                                <div v-if="!selectedDelivery.has_medical_report" class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                    * Laudo não disponível para este exame
                                </div>
                            </div>

                            <!-- Print Status - Show after printing -->
                            <div v-if="hasPrintedSomething" class="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                <div class="flex items-center space-x-2 mb-3">
                                    <CheckIcon class="w-4 h-4 text-green-600 dark:text-green-400" />
                                    <span class="text-sm font-medium text-green-700 dark:text-green-300">
                                        Impressão realizada! Agora você pode confirmar a entrega:
                                    </span>
                                </div>
                                <div class="flex space-x-2 text-xs">
                                    <span v-if="imagesPrinted" class="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full">
                                        ✓ Imagens impressas
                                    </span>
                                    <span v-if="reportPrinted" class="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full">
                                        ✓ Laudo impresso
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Footer -->
                        <div class="flex justify-end space-x-3 p-4 border-t border-gray-200 dark:border-gray-700">
                            <button 
                                @click="closeDeliveryModal"
                                class="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors font-medium rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                            >
                                Cancelar
                            </button>
                            <button 
                                @click="confirmDelivery"
                                :disabled="deliveryLoading || !isDeliveryFormValid"
                                class="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 flex items-center space-x-2"
                            >
                                <CheckIcon v-if="!deliveryLoading" class="w-4 h-4" />
                                <div v-else class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>{{ deliveryLoading ? 'Confirmando...' : 'Confirmar Entrega' }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </Transition>

            <!-- Delivery Details Modal -->
            <Transition
                enter-active-class="transition duration-300 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-200 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
            >
                <div v-if="showDetailsModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click="closeDetailsModal">
                    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full mx-4 border border-gray-200 dark:border-gray-700 transform transition-all" @click.stop>
                        <!-- Header -->
                        <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                            <div class="flex items-center space-x-3">
                                <div class="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                                    <EyeIcon class="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                </div>
                                <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Detalhes da Entrega</h3>
                            </div>
                            <button 
                                @click="closeDetailsModal"
                                class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                aria-label="Fechar modal"
                            >
                                <XIcon class="w-4 h-4" />
                            </button>
                        </div>
                        
                        <!-- Content -->
                        <div class="p-6">
                            <div v-if="selectedDelivery" class="space-y-6">
                                <!-- Patient Info -->
                                <div class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <div class="flex items-center space-x-3 mb-3">
                                        <UserIcon class="w-5 h-5 text-gray-500" />
                                        <h4 class="font-medium text-gray-900 dark:text-gray-100">Informações do Paciente</h4>
                                    </div>
                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Paciente:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedDelivery.patient_name }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">ID:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedDelivery.patient_id }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Exame:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedDelivery.service_name }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">OS:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedDelivery.accession_number || 'N/A' }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Data do Exame:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ formatDate(selectedDelivery.exam_date) }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Status:</span>
                                            <span :class="getStatusClass(selectedDelivery.status)" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                                                {{ getStatusText(selectedDelivery.status) }}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Delivery Info -->
                                <div class="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                    <div class="flex items-center space-x-3 mb-3">
                                        <PackageCheckIcon class="w-5 h-5 text-green-600 dark:text-green-400" />
                                        <h4 class="font-medium text-green-900 dark:text-green-100">Informações da Entrega</h4>
                                    </div>
                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Entregue para:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedDelivery.delivered_to || 'N/A' }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Documento:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedDelivery.delivered_to_document || 'N/A' }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Parentesco:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedDelivery.relationship || 'N/A' }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Data da Entrega:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedDelivery.delivery_date ? formatDate(selectedDelivery.delivery_date) : 'N/A' }}</span>
                                        </div>
                                        <div class="flex justify-between">
  <span class="text-gray-600 dark:text-gray-400">Entregue por:</span>
  <span class="font-medium text-gray-900 dark:text-gray-100">
    {{ selectedDelivery.delivered_by_info?.name || 'N/A' }}
  </span>
</div>

                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Data de Prontidão:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedDelivery.ready_date ? formatDate(selectedDelivery.ready_date) : 'N/A' }}</span>
                                        </div>
                                    </div>
                                    <div v-if="selectedDelivery.delivery_notes" class="mt-3 pt-3 border-t border-green-200 dark:border-green-700">
                                        <div class="flex items-start space-x-2">
                                            <StickyNoteIcon class="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <span class="text-gray-600 dark:text-gray-400 text-sm">Observações:</span>
                                                <p class="text-gray-900 dark:text-gray-100 text-sm mt-1">{{ selectedDelivery.delivery_notes }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Footer -->
                        <div class="flex justify-end space-x-3 p-4 border-t border-gray-200 dark:border-gray-700">
                            <button 
                                @click="printImagesFromDetails"
                                :disabled="printLoading"
                                class="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 flex items-center space-x-2"
                            >
                                <ImageIcon v-if="!printLoading" class="w-4 h-4" />
                                <div v-else class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>{{ printLoading ? 'Processando...' : 'Visualizar Imagens' }}</span>
                            </button>
                            <button 
                                @click="downloadReportFromDetails"
                                :disabled="downloadLoading"
                                class="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 flex items-center space-x-2"
                            >
                                <FileTextIcon v-if="!downloadLoading" class="w-4 h-4" />
                                <div v-else class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>{{ downloadLoading ? 'Processando...' : 'Visualizar Laudo' }}</span>
                            </button>
                            <button 
                                @click="closeDetailsModal"
                                class="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors font-medium rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            </Transition>
            </div>
        </template>
    </BaseTemplate>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted } from 'vue'
import BaseTemplate from '../templates/BaseTemplate.vue'
import { useExamDeliveryStore } from '../../stores/examDelivery'
import { useClinicStore } from '../../stores/settings/clinicStore'
import { 
  
    CheckIcon,
    XIcon,
    InfoIcon,
    AlertCircleIcon,
    EyeIcon,
} from 'lucide-vue-next'
import BannerMessage from '@/components/BannerMessage.vue';

const examDeliveryStore = useExamDeliveryStore()
const clinicStore = useClinicStore()

// Reactive data
const loading = ref(false)
const deliveries = ref([])
const searchType = ref('patient_name')
const searchQuery = ref('')
const statusFilter = ref('')
const showDeliveryModal = ref(false)
const showDetailsModal = ref(false)
const selectedDelivery = ref(null)
const printLoading = ref(false)
const downloadLoading = ref(false)
const deliveryLoading = ref(false)
const saveLoading = ref(false)
const isDataSaved = ref(false)
const imagesPrinted = ref(false)
const reportPrinted = ref(false)
const pagination = ref({
    offset: 0,
    limit: 20,
    count: 0
})

// Banner system
const showBanner = ref(false)
const bannerType = ref('success')
const bannerMessage = ref('')

const deliveryForm = ref({
    delivered_to: '',
    delivered_to_document: '',
    relationship: '',
    delivery_notes: ''
})

// Computed
const searchFilters = computed(() => {
    return {
        search_type: searchType.value.trim(),
        query: searchQuery.value.trim(),
        status: statusFilter.value.trim()
    }
})

const isDeliveryFormValid = computed(() => {
    return deliveryForm.value.delivered_to.trim() !== '' && 
           deliveryForm.value.delivered_to_document.trim() !== '' &&
           deliveryForm.value.relationship.trim() !== ''
})

// Banner function
const showBannerMessage = (type, message) => {
    bannerType.value = type
    bannerMessage.value = message
    showBanner.value = true
}



// Methods
const loadDeliveries = async () => {
    loading.value = true
    try {
        let response
        
        // Usar busca direta no backend quando houver filtros de pesquisa

        response = await examDeliveryStore.searchDeliveries({
            offset: pagination.value.offset,
            limit: pagination.value.limit,
            ...searchFilters.value
        })
        
        deliveries.value = response.items || []
        pagination.value = {
            offset: response.offset || 0,
            limit: response.limit || 20,
            count: response.count || 0
        }
    } catch (error) {
        console.error('Erro ao carregar entregas:', error)
        showBannerMessage('error', 'Erro ao carregar lista de entregas. Tente novamente.')
    } finally {
        loading.value = false
    }
}

const searchDeliveries = () => {
    pagination.value.offset = 0
    loadDeliveries()
}

const clearFilters = () => {
    searchType.value = 'patient_name'
    searchQuery.value = ''
    statusFilter.value = ''
    pagination.value.offset = 0
    loadDeliveries()
}

const previousPage = () => {
    if (pagination.value.offset > 0) {
        pagination.value.offset -= pagination.value.limit
        loadDeliveries()
    }
}

const nextPage = () => {
    if (pagination.value.offset + deliveries.value.length < pagination.value.count) {
        pagination.value.offset += pagination.value.limit
        loadDeliveries()
    }
}

const openDeliveryModal = (delivery) => {
    selectedDelivery.value = delivery
    deliveryForm.value = {
        delivered_to: delivery.delivered_to || '',
        delivered_to_document: delivery.delivered_to_document || '',
        relationship: delivery.relationship || '',
        delivery_notes: delivery.delivery_notes || ''
    }
    // Reset print status
    imagesPrinted.value = false
    reportPrinted.value = false
    isDataSaved.value = !!(delivery.delivered_to && delivery.delivered_to_document && delivery.relationship)
    showDeliveryModal.value = true
}

const closeDeliveryModal = () => {
    showDeliveryModal.value = false
    selectedDelivery.value = null
    // Limpar formulário
    deliveryForm.value = {
        delivered_to: '',
        delivered_to_document: '',
        relationship: '',
        delivery_notes: ''
    }
    deliveryLoading.value = false
    saveLoading.value = false
    isDataSaved.value = false
    imagesPrinted.value = false
    reportPrinted.value = false
}

// Novas funções de impressão
const printImages = async () => {
    try {
        printLoading.value = true
        await examDeliveryStore.generateImagesPDF(selectedDelivery.value.id)
        
        // Marcar como impresso no backend
        await examDeliveryStore.confirmPrint(selectedDelivery.value.id, { print_type: 'images' })
        
        imagesPrinted.value = true
        loadDeliveries()
        showBannerMessage('success', 'Imagens impressas com sucesso!')
    } catch (error) {
        console.error('Erro ao imprimir imagens:', error)
        showBannerMessage('error', 'Erro ao imprimir imagens. Tente novamente.')
    } finally {
        printLoading.value = false
    }
}

const printReport = async () => {
    try {
        downloadLoading.value = true
        await examDeliveryStore.generatePDFReport(selectedDelivery.value.id)
        
        // Marcar como impresso no backend
        await examDeliveryStore.confirmPrint(selectedDelivery.value.id, { print_type: 'report' })
        
        reportPrinted.value = true
        loadDeliveries()
        showBannerMessage('success', 'Laudo impresso com sucesso!')
    } catch (error) {
        console.error('Erro ao imprimir laudo:', error)
        showBannerMessage('error', 'Erro ao imprimir laudo. Verifique se o laudo possui conteúdo.')
    } finally {
        downloadLoading.value = false
    }
}

const viewDeliveryDetails = (delivery) => {
    console.log('Delivery details:', delivery)
    selectedDelivery.value = delivery
    showDetailsModal.value = true
}

const closeDetailsModal = () => {
    showDetailsModal.value = false
    selectedDelivery.value = null
}

const confirmDelivery = async () => {
    try {
        deliveryLoading.value = true
        
        // Validar se todos os campos obrigatórios estão preenchidos
        if (!isDeliveryFormValid.value) {
            showBannerMessage('error', 'Por favor, preencha todos os campos obrigatórios.')
            return
        }
        
        // Preparar dados para envio
        const deliveryData = {
            delivered_to: deliveryForm.value.delivered_to.trim(),
            delivered_to_document: deliveryForm.value.delivered_to_document.trim(),
            relationship: deliveryForm.value.relationship.trim(),
            delivery_notes: deliveryForm.value.delivery_notes.trim()
        }
        
        await examDeliveryStore.deliverExam(selectedDelivery.value.id, deliveryData)
        closeDeliveryModal()
        loadDeliveries()
        showBannerMessage('success', 'Entrega confirmada com sucesso!')
    } catch (error) {
        console.error('Erro ao confirmar entrega:', error)
        showBannerMessage('error', 'Erro ao confirmar entrega. Tente novamente.')
    } finally {
        deliveryLoading.value = false
    }
}

const markAsReady = async (delivery) => {
    try {
        await examDeliveryStore.markAsReady(delivery.id)
        loadDeliveries()
        showBannerMessage('success', 'Exame marcado como pronto para entrega!')
    } catch (error) {
        console.error('Erro ao marcar como pronto:', error)
        showBannerMessage('error', 'Erro ao marcar exame como pronto. Tente novamente.')
    }
}



const printImagesFromDetails = async () => {
    try {
        printLoading.value = true
        await examDeliveryStore.generateImagesPDF(selectedDelivery.value.id)
        // Não fecha o modal, apenas atualiza a lista
        loadDeliveries()
        showBannerMessage('success', 'Imagens processadas com sucesso!')
    } catch (error) {
        console.error('Erro ao imprimir imagens:', error)
        showBannerMessage('error', 'Erro ao processar imagens. Tente novamente.')
    } finally {
        printLoading.value = false
    }
}

const downloadReportFromDetails = async () => {
    try {
        downloadLoading.value = true
        await examDeliveryStore.generatePDFReport(selectedDelivery.value.id)
        // Não fecha o modal, apenas atualiza a lista
        loadDeliveries()
        showBannerMessage('success', 'Laudo processado com sucesso!')
    } catch (error) {
        console.error('Erro ao visualizar laudo:', error)
        showBannerMessage('error', 'Erro ao processar laudo. Verifique se o laudo possui conteúdo.')
    } finally {
        downloadLoading.value = false
    }
}








const getStatusClass = (status) => {
    const baseClasses = 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full'
    const classes = {
        pending: `bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 ${baseClasses}`,
        ready: `bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 ${baseClasses}`,
        delivered: `bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 ${baseClasses}`,
        returned: `bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 ${baseClasses}`
    }
    return classes[status] || `bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 ${baseClasses}`
}

const getStatusText = (status) => {
    const texts = {
        pending: 'Pendente',
        ready: 'Pronto',
        delivered: 'Entregue',
        returned: 'Devolvido'
    }
    return texts[status] || status
}

const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
}

const formatCPF = (cpf) => {
    if (!cpf) return ''
    const clean = cpf.replace(/\D/g, '')
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

// Lifecycle
onMounted(() => {
    // Aplica os estilos do ClinicStore
    clinicStore.applyClinicStyles()
    loadDeliveries()
})

// Adicionar listener para tecla ESC
const handleEscape = (event) => {
    if (event.key === 'Escape') {
        if (showDeliveryModal.value) {
            closeDeliveryModal()
        } else if (showDetailsModal.value) {
            closeDetailsModal()
        }
    }
}

onMounted(() => {
    document.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
    document.removeEventListener('keydown', handleEscape)
})
</script>

<style scoped>
/* Estilos específicos do componente */
</style>
